import { Transaction, UserSettings } from '../types';
import { ledgerDb } from './firebase';
import { 
  collection, 
  addDoc, 
  getDocs, 
  deleteDoc, 
  doc, 
  query, 
  orderBy,
  updateDoc,
  setDoc,
  getDoc
} from 'firebase/firestore';

const STORAGE_KEY_PREFIX = 'smart_ledger_expenses_';
const SETTINGS_KEY_PREFIX = 'smart_ledger_settings_';
const PARENT_COLLECTION_NAME = 'ledgers';
const SUB_COLLECTION_NAME = 'records';
const SETTINGS_DOC_NAME = 'settings';

// LocalStorage implementation (Fallback)
const localStore = {
  async addTransaction(userId: string, transaction: Omit<Transaction, 'id' | 'createdAt'>): Promise<Transaction> {
    await new Promise(resolve => setTimeout(resolve, 300));
    const newTransaction: Transaction = {
      ...transaction,
      id: crypto.randomUUID(),
      createdAt: Date.now(),
    } as Transaction;
    const existingData = await this.getTransactions(userId);
    const updatedData = [newTransaction, ...existingData];
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${userId}`, JSON.stringify(updatedData));
    return newTransaction;
  },

  async getTransactions(userId: string): Promise<Transaction[]> {
    await new Promise(resolve => setTimeout(resolve, 200));
    const data = localStorage.getItem(`${STORAGE_KEY_PREFIX}${userId}`);
    if (!data) return [];
    try {
      const parsed = JSON.parse(data);
      // Migration for old data without 'type'
      const migrated = parsed.map((item: any) => ({
        ...item,
        type: item.type || 'expense'
      }));
      return migrated.sort((a: Transaction, b: Transaction) => 
        new Date(b.date).getTime() - new Date(a.date).getTime()
      );
    } catch (e) {
      console.error("Failed to parse storage", e);
      return [];
    }
  },

  async updateTransaction(userId: string, transaction: Transaction): Promise<void> {
    const existingData = await this.getTransactions(userId);
    const updatedData = existingData.map(t => t.id === transaction.id ? transaction : t);
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${userId}`, JSON.stringify(updatedData));
  },

  async deleteTransaction(userId: string, id: string): Promise<void> {
    const existingData = await this.getTransactions(userId);
    const updatedData = existingData.filter(e => e.id !== id);
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${userId}`, JSON.stringify(updatedData));
  },

  async getSettings(userId: string): Promise<UserSettings> {
    const data = localStorage.getItem(`${SETTINGS_KEY_PREFIX}${userId}`);
    if (!data) return { monthlyBudget: 0 };
    return JSON.parse(data);
  },

  async updateSettings(userId: string, settings: UserSettings): Promise<void> {
    localStorage.setItem(`${SETTINGS_KEY_PREFIX}${userId}`, JSON.stringify(settings));
  }
};

// Firestore Implementation
const firestoreService = {
  async addTransaction(userId: string, transaction: Omit<Transaction, 'id' | 'createdAt'>): Promise<Transaction> {
    if (!ledgerDb) throw new Error("Firestore not initialized");

    // Remove undefined fields which Firestore does not support
    const sanitizedTransaction = Object.entries(transaction).reduce((acc, [key, value]) => {
        if (value !== undefined) {
            acc[key] = value;
        }
        return acc;
    }, {} as Record<string, any>);

    const newTransactionData = {
      ...sanitizedTransaction,
      createdAt: Date.now(),
    };

    const subCollectionRef = collection(ledgerDb, PARENT_COLLECTION_NAME, userId, SUB_COLLECTION_NAME);
    const docRef = await addDoc(subCollectionRef, newTransactionData);
    
    return {
      id: docRef.id,
      ...newTransactionData
    } as Transaction;
  },

  async getTransactions(userId: string): Promise<Transaction[]> {
    if (!ledgerDb) throw new Error("Firestore not initialized");

    const subCollectionRef = collection(ledgerDb, PARENT_COLLECTION_NAME, userId, SUB_COLLECTION_NAME);
    const q = query(
      subCollectionRef, 
      orderBy('date', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const transactions: Transaction[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      transactions.push({
        id: doc.id,
        type: data.type || 'expense', // Default to expense for legacy data
        amount: data.amount,
        place: data.place,
        method: data.method,
        sourceMethod: data.sourceMethod,
        sourcePaymentDetail: data.sourcePaymentDetail,
        pointProvider: data.pointProvider,
        isPointUsage: data.isPointUsage,
        paymentDetail: data.paymentDetail,
        category: data.category,
        items: data.items, // Map the items field
        date: data.date,
        note: data.note,
        createdAt: data.createdAt,
      } as Transaction);
    });

    return transactions;
  },

  async updateTransaction(userId: string, transaction: Transaction): Promise<void> {
    if (!ledgerDb) throw new Error("Firestore not initialized");
    const { id, ...data } = transaction;

    // Remove undefined fields which Firestore does not support
    const sanitizedData = Object.entries(data).reduce((acc, [key, value]) => {
        if (value !== undefined) {
            acc[key] = value;
        }
        return acc;
    }, {} as Record<string, any>);

    const docRef = doc(ledgerDb, PARENT_COLLECTION_NAME, userId, SUB_COLLECTION_NAME, id);
    await updateDoc(docRef, sanitizedData);
  },

  async deleteTransaction(userId: string, id: string): Promise<void> {
    if (!ledgerDb) throw new Error("Firestore not initialized");
    await deleteDoc(doc(ledgerDb, PARENT_COLLECTION_NAME, userId, SUB_COLLECTION_NAME, id));
  },

  async getSettings(userId: string): Promise<UserSettings> {
    if (!ledgerDb) throw new Error("Firestore not initialized");
    const docRef = doc(ledgerDb, PARENT_COLLECTION_NAME, userId, 'settings', 'general');
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data() as UserSettings;
    }
    return { monthlyBudget: 0 };
  },

  async updateSettings(userId: string, settings: UserSettings): Promise<void> {
    if (!ledgerDb) throw new Error("Firestore not initialized");
    const docRef = doc(ledgerDb, PARENT_COLLECTION_NAME, userId, 'settings', 'general');
    await setDoc(docRef, settings, { merge: true });
  }
};

// Helper to get balance key
const getBalanceKey = (method: string, detail?: string) => {
  if (detail && detail !== 'その他') {
    return `${method}:${detail}`;
  }
  return method;
};

// Helper to update balance based on transaction
const calculateNewBalances = (
  currentBalances: Record<string, number>,
  transaction: Omit<Transaction, 'id' | 'createdAt'>,
  isReversal: boolean = false
): Record<string, number> => {
  const newBalances = { ...currentBalances };
  const multiplier = isReversal ? -1 : 1;
  const amount = transaction.amount;

  const updateBalance = (method: string, detail: string | undefined, delta: number) => {
      const key = getBalanceKey(method, detail);
      newBalances[key] = (newBalances[key] || 0) + delta;
  };

  if (transaction.type === 'adjustment') {
    // adjustment type: balance was already set directly in settings before addTransaction was called.
    // Skip auto-update here to prevent double-counting.
    return newBalances;
  } else if (transaction.type === 'expense') {
    updateBalance(transaction.method, transaction.paymentDetail, -amount * multiplier);
  } else if (transaction.type === 'income') {
    updateBalance(transaction.method, transaction.paymentDetail, amount * multiplier);
  } else if (transaction.type === 'charge') {
    // Charge: Source decreases, Destination (method) increases
    if (transaction.sourceMethod) {
      updateBalance(transaction.sourceMethod, transaction.sourcePaymentDetail, -amount * multiplier);
    }
    updateBalance(transaction.method, transaction.paymentDetail, amount * multiplier);
  } else if (transaction.type === 'point') {
    // Point usage decreases balance, earning increases
    if (transaction.method === 'ポイント') { 
        if (transaction.isPointUsage) {
            updateBalance('ポイント', undefined, -amount * multiplier);
        } else {
            updateBalance('ポイント', undefined, amount * multiplier);
        }
    }
  }

  return newBalances;
};

// Export wrapper that chooses implementation
export const storageService = {
  async addTransaction(userId: string, transaction: Omit<Transaction, 'id' | 'createdAt'>): Promise<Transaction> {
    let result: Transaction;
    if (userId === 'guest' || !ledgerDb) {
      result = await localStore.addTransaction(userId, transaction);
    } else {
      result = await firestoreService.addTransaction(userId, transaction);
    }

    // Auto-update balance
    try {
        const settings = await this.getSettings(userId);
        if (settings.balanceSettings?.enabled) {
            const newBalances = calculateNewBalances(settings.balanceSettings.balances, transaction);
            await this.updateSettings(userId, {
                ...settings,
                balanceSettings: {
                    ...settings.balanceSettings,
                    balances: newBalances
                }
            });
        }
    } catch (e) {
        console.error("Failed to update balance", e);
    }

    return result;
  },

  async getTransactions(userId: string): Promise<Transaction[]> {
    if (userId === 'guest' || !ledgerDb) {
      return localStore.getTransactions(userId);
    }
    return firestoreService.getTransactions(userId);
  },

  async updateTransaction(userId: string, transaction: Transaction): Promise<void> {
    // Get old transaction to reverse its effect
    const transactions = await this.getTransactions(userId);
    const oldTransaction = transactions.find(t => t.id === transaction.id);

    if (userId === 'guest' || !ledgerDb) {
      await localStore.updateTransaction(userId, transaction);
    } else {
      await firestoreService.updateTransaction(userId, transaction);
    }

    // Auto-update balance
    if (oldTransaction) {
        try {
            const settings = await this.getSettings(userId);
            if (settings.balanceSettings?.enabled) {
                // Reverse old transaction
                let balances = calculateNewBalances(settings.balanceSettings.balances, oldTransaction, true);
                // Apply new transaction
                balances = calculateNewBalances(balances, transaction, false);
                
                await this.updateSettings(userId, {
                    ...settings,
                    balanceSettings: {
                        ...settings.balanceSettings,
                        balances: balances
                    }
                });
            }
        } catch (e) {
            console.error("Failed to update balance", e);
        }
    }
  },

  async deleteTransaction(userId: string, id: string): Promise<void> {
    // Get old transaction to reverse its effect
    const transactions = await this.getTransactions(userId);
    const oldTransaction = transactions.find(t => t.id === id);

    if (userId === 'guest' || !ledgerDb) {
      await localStore.deleteTransaction(userId, id);
    } else {
      await firestoreService.deleteTransaction(userId, id);
    }

    // Auto-update balance
    if (oldTransaction) {
        try {
            const settings = await this.getSettings(userId);
            if (settings.balanceSettings?.enabled) {
                // Reverse old transaction
                const balances = calculateNewBalances(settings.balanceSettings.balances, oldTransaction, true);
                
                await this.updateSettings(userId, {
                    ...settings,
                    balanceSettings: {
                        ...settings.balanceSettings,
                        balances: balances
                    }
                });
            }
        } catch (e) {
            console.error("Failed to update balance", e);
        }
    }
  },

  async getSettings(userId: string): Promise<UserSettings> {
    if (userId === 'guest' || !ledgerDb) {
      return localStore.getSettings(userId);
    }
    return firestoreService.getSettings(userId);
  },

  async updateSettings(userId: string, settings: UserSettings): Promise<void> {
    if (userId === 'guest' || !ledgerDb) {
      return localStore.updateSettings(userId, settings);
    }
    return firestoreService.updateSettings(userId, settings);
  }
};